import axios, { AxiosRequestConfig, ResponseType } from "axios";
import qs from "qs";
import { removeFalsy } from "@/utils/help";
import { generateSign } from "@/utils/sign";
import {
  API_PREFIX_JAVA,
  API_PREFIX_JAVA_REDIRECT,
  API_PREFIX_PHP_ERP,
  API_PREFIX_PHP_OS,
  API_REDIRECT_LIST,
  EnvMap,
  ERROR_CODE,
  REGEXP_API_TRANSFER,
  SUCCESS_CODE_LIST,
} from "@/constant";
import {
  getCurrentShopData,
  getLocalEnvStorage,
  getToken,
} from "@/utils/storage";
import { navLogin } from "@/utils/nav";
import { message } from "@/components/EscapeAntd";

export interface ExtraRequestOptions extends AxiosRequestConfig {
  /**
   * 是否驼峰转下划线
   * deprecated
   */
  snakeCase?: boolean;
  /**
   * deprecated
   * 请使用 showErrorMessage
   **/
  showMessage?: boolean;
  /**
   * 显示错误信息，默认true
   */
  showErrorMessage?: boolean;
  /**
   * 返回数据类型
   */
  responseType?: ResponseType;
}

const AXIOS_INSTANCE = axios.create({
  baseURL: API_URL,
  timeout: 120000,
});

/**
 * 生成 formData 数据
 */
const generateFormData = (target: Record<string, string>): FormData => {
  const formData = new FormData();
  if (target) {
    Object.keys(target).forEach((key) => {
      if (
        ["Array", "Object"].includes(
          Object.prototype.toString.call(target[key]).slice(8, -1),
        )
      ) {
        formData.append(key, JSON.stringify(target[key]));
      } else {
        formData.append(key, target[key] as string | Blob); // Explicitly type target[key] as string | Blob
      }
    });
  }
  return formData;
};

/**
 * 修改请求链接和增加签名
 */
const generateRequestConfig = (
  config: AxiosRequestConfig,
): AxiosRequestConfig => {
  const { method, url = "", data: rawData } = config;
  // 当前时间戳
  const timestamp = Math.floor(Date.now() / 1000);
  // 最终请求路径
  let finalPath = url;
  const requestData =
    method === "GET"
      ? { ...qs.parse(url.split("?")[1]), timestamp }
      : { ...(rawData || {}), timestamp };
  const trulyData = removeFalsy(requestData);
  const sign = generateSign(trulyData);
  const signData = { ...trulyData, sign };
  const formData = generateFormData(signData);
  // Java 端转发接口接收参数一定要 formData 形式，不支持json
  config.headers = { ...config.headers, "Content-Type": "multipart/form-data" };
  config.data = formData;
  if (method === "GET") {
    finalPath = `${url}${
      /\?/.test(url) ? "&" : "?"
    }timestamp=${timestamp}&sign=${sign}`;
  }
  // 商城环境增加接口前缀
  if (API_REDIRECT_LIST.some((item) => url.startsWith(item))) {
    // 如果是下载上传类，直接通过java透传转发
    finalPath = `${API_PREFIX_JAVA_REDIRECT}${url}`;
  } else if (REGEXP_API_TRANSFER.test(url)) {
    // 普通接口，直接通过java转发
    finalPath = `${API_PREFIX_JAVA}${url}`;
  }

  // 覆盖请求url
  config.url = finalPath;

  return config;
};

/**
 * 添加请求环境和增加公共请求头参数
 */
const generateRequestHeaders = (
  config: AxiosRequestConfig,
): AxiosRequestConfig => {
  const currentShop = getCurrentShopData();
  /**
   * 添加请求头公共参数
   * supplier：等于 token
   * shopId：当前选择的店铺 id
   * LOCAL_ENV: 联调环境
   */
  config.headers = {
    ...config.headers,
    supplier: getToken() || "",
    shopId: currentShop.shopId || "",
  };
  if (LOCAL_ENV) {
    if (config.url?.includes(`${API_PREFIX_PHP_ERP}/`)) {
      const {
        erp,
        erpHost = "",
        erpSuffix = EnvMap.erp.suffix[0],
      } = getLocalEnvStorage();
      config.headers["LOCAL-ENV"] = `${erpHost}${erp || ""}${erpSuffix}`;
    } else if (config.url?.includes(`${API_PREFIX_PHP_OS}/`)) {
      const {
        os,
        osHost = "",
        osSuffix = EnvMap.os.suffix[0],
      } = getLocalEnvStorage();

      config.headers["LOCAL-ENV"] = `${osHost}${os || ""}${osSuffix}`;
    }
  }
  return config;
};

AXIOS_INSTANCE.interceptors.request.use((config) => {
  const requestConfig = generateRequestConfig(config);
  return generateRequestHeaders(requestConfig);
});

AXIOS_INSTANCE.interceptors.response.use((response) => {
  if (Object.prototype.toString.call(response.data).slice(8, -1) === "Object") {
    const { showErrorMessage = true, showMessage = true } = response.config;
    const { code, msg } = response.data;
    const success = SUCCESS_CODE_LIST.includes(code);
    if (code === ERROR_CODE.notLoggedIn) {
      // 未登录
      navLogin();
    } else if (!success && (showErrorMessage || showMessage)) {
      // 错误提示
      message.error(msg);
    }
    if (success) {
      response.success = true;
    } else {
      // 业务错误抛出
      response.data = Promise.reject(response.data);
    }
    return response;
  } else {
    return response;
  }
});

export const axiosInstance = <T>(
  config: AxiosRequestConfig,
  options?: ExtraRequestOptions,
): Promise<T> => {
  return AXIOS_INSTANCE({
    ...config,
    ...(options || {}),
  }).then(
    (res) => res.data,
    (err) => Promise.reject(err),
  );
};

export default axiosInstance;
